import React, { type ReactNode, useCallback, useId } from 'react';
import { cn } from '@/ui/lib/utils';
import './checkbox.css';

export interface CheckboxProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    name?: string;
    className?: string;
    ariaLabel?: string;
    label?: ReactNode;
}

export const Checkbox = ({
    checked,
    onChange,
    disabled = false,
    name,
    className,
    ariaLabel,
    label,
}: CheckboxProps) => {
    const id = useId();

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            if (disabled) return;
            onChange(e.target.checked);
        },
        [onChange, disabled],
    );

    return (
        <label
            className={cn('checkbox', disabled && 'checkbox--disabled', className)}
            htmlFor={id}
            aria-disabled={disabled}
            aria-label={ariaLabel}
        >
            <input
                type="checkbox"
                checked={checked}
                onChange={handleChange}
                disabled={disabled}
                id={id}
                name={name}
                className="checkbox__input"
                aria-checked={checked}
                tabIndex={disabled ? -1 : 0}
            />
            <span className="checkbox__box" aria-hidden="true">
                {checked ? (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                    >
                        <path
                            d="M15 0C16.6569 1.93277e-07 18 1.34315 18 3V15C18 16.6569 16.6569 18 15 18H3C1.34315 18 4.832e-08 16.6569 0 15V3C1.9328e-07 1.34315 1.34315 4.83192e-08 3 0H15ZM14.5713 5.29297C14.1808 4.90258 13.5467 4.9025 13.1562 5.29297L7.14551 11.3037L4.70703 9.13574C4.31664 8.74583 3.68339 8.74588 3.29297 9.13574C2.9025 9.52621 2.90262 10.1602 3.29297 10.5508L6.42871 13.415C6.81917 13.8055 7.45321 13.8054 7.84375 13.415C7.92462 13.3341 8.01576 13.2635 8.09668 13.1826C8.17738 13.1019 14.529 6.75027 14.5713 6.70801C14.9618 6.31748 14.9618 5.68349 14.5713 5.29297Z"
                            fill="white"
                        />
                    </svg>
                ) : (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                    >
                        <rect
                            x="1"
                            y="1"
                            width="16"
                            height="16"
                            rx="2"
                            stroke="white"
                            strokeWidth="2"
                        />
                    </svg>
                )}
            </span>
            {label && <span className="checkbox__label">{label}</span>}
        </label>
    );
};
